import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { translations, type LanguageCode } from './dictionaries';

type LanguageContextValue = {
	lang: LanguageCode;
	setLang: (lang: LanguageCode) => void;
	t: (key: string, params?: Record<string, string | number>) => string;
};

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
	const [lang, setLangState] = useState<LanguageCode>('en');

	useEffect(() => {
		const saved = localStorage.getItem('lang') as LanguageCode | null;
		if (saved === 'en' || saved === 'zh') {
			setLangState(saved);
		}
	}, []);

	const setLang = (next: LanguageCode) => {
		setLangState(next);
		localStorage.setItem('lang', next);
	};

	const t = useMemo(() => {
		return (key: string, params?: Record<string, string | number>) => {
			const dict = translations[lang] || translations.en;
			let value = dict[key] ?? translations.en[key] ?? key;
			if (params) {
				Object.entries(params).forEach(([k, v]) => {
					value = value.replace(`{${k}}`, String(v));
				});
			}
			return value;
		};
	}, [lang]);

	const value: LanguageContextValue = { lang, setLang, t };
	return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
	const ctx = useContext(LanguageContext);
	if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
	return ctx;
}


